import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Truck,
  Droplet,
  RotateCcw,
  Gauge,
  Calendar,
  Clock,
  TrendingUp,
  RefreshCw,
  Phone,
  Mail,
  Timer,
  Users,
  SlidersHorizontal,
  ArrowLeft,
} from "lucide-react";
import { LeadForm } from "@/components/LeadForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_MINUTES_PER_TRIP = 45;
const DEFAULT_PEOPLE_IN_VEHICLE = 1;
const LABOR_COST_PER_HOUR = 32;

// Fallback client-side calculation (mirrors backend logic exactly)
const computeLocal = (fleet, gallons, trips, minutes, people) => {
  const fleetSize = Number(fleet) || 0;
  const monthlyGallons = Number(gallons) || 0;
  const tripsPerWeek = Number(trips) || 0;
  const mins = Number(minutes) > 0 ? Number(minutes) : DEFAULT_MINUTES_PER_TRIP;
  const peopleCount =
    Number(people) >= 1 ? Math.floor(Number(people)) : DEFAULT_PEOPLE_IN_VEHICLE;

  if (fleetSize <= 0 || tripsPerWeek <= 0) {
    return {
      avg_gallons_per_fillup: 0,
      annual_fuel_consumption: 0,
      labor_savings: 0,
      fuel_time_savings_hours: 0,
      avg_savings_per_vehicle: 0,
    };
  }

  const monthlyTripsTotal = fleetSize * tripsPerWeek * 4.33;
  const annualTripsTotal = fleetSize * tripsPerWeek * 52;
  const hoursSaved = annualTripsTotal * (mins / 60);
  const laborSavings = hoursSaved * LABOR_COST_PER_HOUR * peopleCount;

  return {
    avg_gallons_per_fillup:
      monthlyTripsTotal > 0 ? monthlyGallons / monthlyTripsTotal : 0,
    annual_fuel_consumption: monthlyGallons * 12,
    labor_savings: laborSavings,
    fuel_time_savings_hours: hoursSaved,
    avg_savings_per_vehicle: fleetSize > 0 ? laborSavings / fleetSize : 0,
  };
};

const fmtMoney = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n || 0);

const fmtNumber = (n, digits = 0) =>
  new Intl.NumberFormat("en-US", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(n || 0);

const InputField = ({
  id,
  label,
  hint,
  icon: Icon,
  value,
  onChange,
  placeholder,
  unit,
  testId,
  badge,
  min = "0",
  step,
}) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between gap-2">
      <label
        htmlFor={id}
        className="block text-sm font-semibold text-[#404041]"
      >
        {label}
      </label>
      {badge && (
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#ecac1f]/15 text-[#7a5b00] border border-[#ecac1f]/30">
          {badge}
        </span>
      )}
    </div>
    <div className="relative">
      <div className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none text-[#63499d]">
        <Icon className="w-5 h-5" strokeWidth={2} />
      </div>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        min={min}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="brand-input numeric w-full pl-11 pr-20 py-3.5 text-lg font-semibold text-[#404041] bg-white border-2 border-slate-200 rounded-xl focus:border-[#63499d] focus:outline-none transition-colors"
        data-testid={testId}
      />
      {unit && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-xs font-semibold text-slate-400 uppercase tracking-wider">
          {unit}
        </div>
      )}
    </div>
    {hint && <p className="text-xs text-slate-500 leading-relaxed">{hint}</p>}
  </div>
);

const ResultCard = ({
  label,
  value,
  sublabel,
  icon: Icon,
  accent = "purple",
  testId,
}) => {
  const accentClasses = {
    purple: "bg-[#63499d] text-white",
    yellow: "bg-[#ecac1f] text-[#2a1f4a]",
    dark: "bg-[#404041] text-white",
  };
  return (
    <div
      className="result-card bg-white border border-slate-200 rounded-2xl p-5 sm:p-6"
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-4">
        <div
          className={`w-10 h-10 rounded-lg grid place-items-center ${accentClasses[accent]}`}
        >
          <Icon className="w-5 h-5" strokeWidth={2.25} />
        </div>
      </div>
      <div className="numeric-large text-3xl sm:text-4xl text-[#404041] mb-1.5 numeric">
        {value}
      </div>
      <div className="text-sm font-semibold text-[#63499d] mb-1">{label}</div>
      {sublabel && (
        <div className="text-xs text-slate-500 leading-relaxed">{sublabel}</div>
      )}
    </div>
  );
};

export const CalculatorAdvanced = () => {
  const [fleetSize, setFleetSize] = useState("10");
  const [monthlyGallons, setMonthlyGallons] = useState("5000");
  const [tripsPerWeek, setTripsPerWeek] = useState("5");
  const [minutesPerTrip, setMinutesPerTrip] = useState(
    String(DEFAULT_MINUTES_PER_TRIP),
  );
  const [peopleInVehicle, setPeopleInVehicle] = useState(
    String(DEFAULT_PEOPLE_IN_VEHICLE),
  );
  const [serverResult, setServerResult] = useState(null);

  const localResult = useMemo(
    () =>
      computeLocal(
        fleetSize,
        monthlyGallons,
        tripsPerWeek,
        minutesPerTrip,
        peopleInVehicle,
      ),
    [fleetSize, monthlyGallons, tripsPerWeek, minutesPerTrip, peopleInVehicle],
  );

  useEffect(() => {
    const f = Number(fleetSize);
    const g = Number(monthlyGallons);
    const t = Number(tripsPerWeek);
    if (f <= 0 || g <= 0 || t <= 0) {
      setServerResult(null);
      return;
    }
    const m = Number(minutesPerTrip);
    const p = Number(peopleInVehicle);
    const handle = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/calculate`, {
          fleet_size: f,
          monthly_gallons: g,
          trips_per_week: t,
          minutes_per_trip: m > 0 ? m : DEFAULT_MINUTES_PER_TRIP,
          people_in_vehicle: p >= 1 ? Math.floor(p) : DEFAULT_PEOPLE_IN_VEHICLE,
        });
        setServerResult(data);
      } catch (err) {
        console.error("calculation api failed", err);
      }
    }, 600);
    return () => clearTimeout(handle);
  }, [
    fleetSize,
    monthlyGallons,
    tripsPerWeek,
    minutesPerTrip,
    peopleInVehicle,
  ]);

  const result = serverResult || localResult;

  const handleReset = () => {
    setFleetSize("");
    setMonthlyGallons("");
    setTripsPerWeek("");
    setMinutesPerTrip(String(DEFAULT_MINUTES_PER_TRIP));
    setPeopleInVehicle(String(DEFAULT_PEOPLE_IN_VEHICLE));
    setServerResult(null);
  };

  // Display values (with sensible fallbacks)
  const minsDisplay =
    Number(minutesPerTrip) > 0
      ? Number(minutesPerTrip)
      : DEFAULT_MINUTES_PER_TRIP;
  const peopleDisplay =
    Number(peopleInVehicle) >= 1
      ? Math.floor(Number(peopleInVehicle))
      : DEFAULT_PEOPLE_IN_VEHICLE;
  const effectiveLaborRate = LABOR_COST_PER_HOUR * peopleDisplay;

  return (
    <section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 sm:-mt-16 relative z-10"
      data-testid="calculator-advanced-section"
    >
      {/* Mode switcher pill */}
      <div className="flex items-center justify-end mb-3 no-print">
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold text-[#63499d] bg-white border border-[#63499d]/25 hover:bg-[#63499d]/5 transition-colors shadow-sm"
          data-testid="back-to-standard-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to Standard Calculator
        </Link>
      </div>

      <div className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-[#63499d]/10 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-5">
          {/* LEFT: Inputs */}
          <div className="lg:col-span-2 p-6 sm:p-8 lg:p-10 bg-gradient-to-br from-[#f7f5fb] to-white border-b lg:border-b-0 lg:border-r border-slate-200">
            <div className="flex items-center gap-3 mb-2">
              <span className="gold-bar" />
              <span className="text-[#63499d] text-xs font-bold tracking-[0.18em] uppercase">
                Step 1 — Advanced Inputs
              </span>
            </div>
            <h2
              className="font-display font-bold text-2xl sm:text-3xl text-[#404041] mb-1"
              data-testid="calculator-advanced-input-title"
            >
              Fine-tune your fleet numbers
            </h2>
            <p className="text-sm text-slate-500 mb-7 leading-relaxed">
              Adjust time-per-trip and crew size for a tailored estimate. Leave
              defaults to match the standard calculator.
            </p>

            <div className="space-y-5">
              <InputField
                id="adv-fleet-size"
                label="Number of Vehicles in Fleet"
                icon={Truck}
                value={fleetSize}
                onChange={setFleetSize}
                placeholder="e.g. 10"
                unit="vehicles"
                testId="adv-input-fleet-size"
              />
              <InputField
                id="adv-monthly-gallons"
                label="Total Gallons Purchased Each Month"
                icon={Droplet}
                value={monthlyGallons}
                onChange={setMonthlyGallons}
                placeholder="e.g. 5,000"
                unit="gallons"
                testId="adv-input-monthly-gallons"
              />
              <InputField
                id="adv-trips-per-week"
                label="Fueling Trips Per Vehicle Per Week"
                hint="Average number of trips each truck makes to the fuel island."
                icon={RotateCcw}
                value={tripsPerWeek}
                onChange={setTripsPerWeek}
                placeholder="e.g. 5"
                unit="trips/wk"
                testId="adv-input-trips-per-week"
              />

              {/* Divider for advanced/optional inputs */}
              <div className="pt-2">
                <div className="flex items-center gap-3">
                  <SlidersHorizontal className="w-4 h-4 text-[#63499d]" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#63499d]">
                    Optional Adjustments
                  </span>
                  <span className="flex-1 h-px bg-slate-200" />
                </div>
              </div>

              <InputField
                id="adv-minutes-per-trip"
                label="Time to Fuel (including drive time)"
                hint={`Total minutes per fueling trip — door-to-door. Default is ${DEFAULT_MINUTES_PER_TRIP} min (15 min out-of-route + 30 min fueling).`}
                icon={Timer}
                value={minutesPerTrip}
                onChange={setMinutesPerTrip}
                placeholder={String(DEFAULT_MINUTES_PER_TRIP)}
                unit="min/trip"
                testId="adv-input-minutes-per-trip"
                badge="Optional"
                min="1"
                step="1"
              />

              <InputField
                id="adv-people-in-vehicle"
                label="Employees in Vehicle when Fueling"
                hint="Default is 1. Each additional employee multiplies the $32/hr labor burden."
                icon={Users}
                value={peopleInVehicle}
                onChange={setPeopleInVehicle}
                placeholder={String(DEFAULT_PEOPLE_IN_VEHICLE)}
                unit="employees"
                testId="adv-input-people-in-vehicle"
                badge="Optional"
                min="1"
                step="1"
              />

              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#63499d] hover:text-[#4d3884] transition-colors no-print"
                data-testid="adv-reset-button"
              >
                <RefreshCw className="w-4 h-4" />
                Reset values
              </button>
            </div>
          </div>

          {/* RIGHT: Results */}
          <div className="lg:col-span-3 p-6 sm:p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-2">
              <span className="gold-bar" />
              <span className="text-[#63499d] text-xs font-bold tracking-[0.18em] uppercase">
                Step 2 — Your Estimated Savings
              </span>
            </div>
            <h2
              className="font-display font-bold text-2xl sm:text-3xl text-[#404041] mb-7"
              data-testid="calculator-advanced-results-title"
            >
              Here&apos;s what Ricochet can do for you
            </h2>

            {/* Headline savings */}
            <div
              className="bg-[#63499d] text-white rounded-2xl p-6 sm:p-7 mb-6 relative overflow-hidden"
              data-testid="adv-headline-savings-card"
            >
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#ecac1f]/20 blur-2xl pointer-events-none" />
              <div className="relative">
                <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#ecac1f] mb-2">
                  Estimated Annual Labor Savings
                </div>
                <div
                  className="numeric-large text-5xl sm:text-6xl mb-2 numeric"
                  data-testid="adv-result-labor-savings"
                >
                  {fmtMoney(result.labor_savings)}
                </div>
                <div className="text-sm text-white/75 max-w-md">
                  Based on ${LABOR_COST_PER_HOUR}/hr fully-burdened labor cost
                  {peopleDisplay > 1 && (
                    <>
                      {" "}
                      × <span className="text-[#ecac1f] font-semibold">{peopleDisplay} employees</span>{" "}
                      (<span className="text-[#ecac1f] font-semibold">{fmtMoney(effectiveLaborRate)}/hr</span> effective)
                    </>
                  )}{" "}
                  vs. retail island fueling.
                </div>
              </div>
            </div>

            {/* Sub results */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ResultCard
                label="Average Gallons Per Fill-Up"
                value={fmtNumber(result.avg_gallons_per_fillup, 1)}
                sublabel="Across your fleet"
                icon={Gauge}
                accent="yellow"
                testId="adv-result-avg-gallons"
              />
              <ResultCard
                label="Annual Fuel Consumption"
                value={`${fmtNumber(result.annual_fuel_consumption)} gal`}
                sublabel="Total gallons per year"
                icon={Calendar}
                accent="dark"
                testId="adv-result-annual-fuel"
              />
              <ResultCard
                label="Fuel Time Saved"
                value={`${fmtNumber(result.fuel_time_savings_hours)} hrs`}
                sublabel={`Assumes ${fmtNumber(minsDisplay)} min per refueling trip eliminated`}
                icon={Clock}
                accent="purple"
                testId="adv-result-time-savings"
              />
              <ResultCard
                label="Average Savings Per Vehicle"
                value={fmtMoney(result.avg_savings_per_vehicle)}
                sublabel="Per fleet vehicle vs. retail island fueling"
                icon={TrendingUp}
                accent="yellow"
                testId="adv-result-avg-per-vehicle"
              />
            </div>

            {/* CTA strip */}
            <div className="mt-7 p-5 sm:p-6 bg-[#f7f5fb] border border-[#63499d]/15 rounded-2xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 no-print">
              <div>
                <div className="font-display font-bold text-lg text-[#404041] leading-tight">
                  Ready to start saving?
                </div>
                <div className="text-sm text-slate-500">
                  Talk to a Ricochet fuel specialist today.
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href="tel:+18339255065"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-[#63499d] bg-white border-2 border-[#63499d]/20 hover:border-[#63499d] transition-colors"
                  data-testid="adv-results-call-cta"
                >
                  <Phone className="w-4 h-4" /> 833-925-5065
                </a>
                <a
                  href="mailto:sales@ricochetfuel.com?subject=Fuel%20Savings%20Inquiry"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white bg-[#ecac1f] hover:bg-[#d99812] text-[#2a1f4a] transition-colors"
                  data-testid="adv-results-email-cta"
                >
                  <Mail className="w-4 h-4" /> Email Sales
                </a>
              </div>
            </div>

            <p
              className="mt-5 text-xs text-slate-400 leading-relaxed max-w-2xl"
              data-testid="adv-results-disclaimer"
            >
              * Calculations are estimates based on $
              {LABOR_COST_PER_HOUR}/hr fully-burdened labor cost,{" "}
              {fmtNumber(minsDisplay)} min per refueling trip, and{" "}
              {peopleDisplay}{" "}
              {peopleDisplay === 1 ? "employee" : "employees"} per fueling stop.
              Actual savings may vary based on your operations, fuel prices,
              and location.
            </p>

            <LeadForm
              fleetSize={fleetSize}
              monthlyGallons={monthlyGallons}
              tripsPerWeek={tripsPerWeek}
              laborSavings={result.labor_savings}
              minutesPerTrip={minsDisplay}
              peopleInVehicle={peopleDisplay}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
